import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, FolderOpen, FileText, ExternalLink,
  Clock, User, Tag, Plus, File, Grid, List,
  Sparkles, Upload, FileCheck, Loader2, X, BookTemplate
} from 'lucide-react';
import { auth } from '../../utils/auth';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from '../../context/LanguageContext';
import { documentsApi } from '../../services';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/all-documents`;

interface Document {
  id: string;
  title: string;
  description?: string;
  file_type?: string;
  file_url?: string;
  case_id?: string;
  case_title?: string;
  client_name?: string;
  category?: string;
  tags?: string[];
  created_by?: string;
  created_at?: string;
  source: 'case' | 'upload' | 'ai_generated';
  content?: string;
}

interface Template {
  id: string;
  title: string;
  description?: string;
  category: string;
  content: string;
  variables: string[];
  is_system: boolean;
  created_by?: string;
  created_at?: string;
  usage_count: number;
  tags?: string[];
}

interface DocumentStats {
  total_documents: number;
  case_files: number;
  ai_generated: number;
  uploads: number;
  templates: {
    user: number;
    system: number;
    total: number;
  };
}

const DocumentsViewMinimal: React.FC = () => {
  const { language } = useTranslation();
  const [activeTab, setActiveTab] = useState<'documents' | 'templates'>('documents');
  const [documents, setDocuments] = useState<Document[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [stats, setStats] = useState<DocumentStats | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [showFilters, setShowFilters] = useState(false);

  // Translations
  const t = {
    documents: language === 'en' ? 'Documents' : 'Dokumenter',
    templates: language === 'en' ? 'Templates' : 'Maler',
    documentsAcrossAllCases: language === 'en' ? 'documents across all cases' : 'dokumenter på tvers av alle saker',
    templatesAvailable: language === 'en' ? 'templates available' : 'maler tilgjengelig',
    newTemplate: language === 'en' ? 'New template' : 'Ny mal',
    total: language === 'en' ? 'Total' : 'Totalt',
    caseDocuments: language === 'en' ? 'Case documents' : 'Saksdokumenter',
    aiGenerated: language === 'en' ? 'AI-generated' : 'AI-generert',
    uploaded: language === 'en' ? 'Uploaded' : 'Opplastede',
    searchPlaceholder: language === 'en' ? 'Search in title, description, case...' : 'Søk i tittel, beskrivelse, sak...',
    searchTemplatesPlaceholder: language === 'en' ? 'Search templates...' : 'Søk i maler...',
    allCategories: language === 'en' ? 'All categories' : 'Alle kategorier',
    allSources: language === 'en' ? 'All sources' : 'Alle kilder',
    cases: language === 'en' ? 'Cases' : 'Saker',
    noDocumentsFound: language === 'en' ? 'No documents found' : 'Ingen dokumenter funnet',
    tryAnotherSearch: language === 'en' ? 'Try another search' : 'Prøv et annet søk',
    documentsWillAppear: language === 'en' ? 'Documents will be shown here when they are added to cases' : 'Dokumenter vil vises her når de blir lagt til saker',
    noTemplatesFound: language === 'en' ? 'No templates found' : 'Ingen maler funnet',
    createFirstTemplate: language === 'en' ? 'Create your first template to get started' : 'Lag din første mal for å komme i gang',
    createTemplate: language === 'en' ? 'Create template' : 'Lag mal',
    noDescription: language === 'en' ? 'No description' : 'Ingen beskrivelse',
    useTemplate: language === 'en' ? 'Use template' : 'Bruk mal',
    variables: language === 'en' ? 'variables' : 'variabler',
    timesUsed: language === 'en' ? 'times used' : 'ganger brukt',
    case: language === 'en' ? 'Case' : 'Sak',
    upload: language === 'en' ? 'Uploaded' : 'Opplastet',
    system: language === 'en' ? 'SYSTEM' : 'SYSTEM'
  };

  useEffect(() => {
    fetchData();
  }, [activeTab, categoryFilter, sourceFilter, searchQuery]);

  const fetchData = async () => {
    try {
      const token = auth.getToken();
      
      if (activeTab === 'documents') {
        // Fetch documents with filters
        const params: any = { limit: 100 };
        if (searchQuery) params.search = searchQuery;
        if (categoryFilter !== 'all') params.category = categoryFilter;
        if (sourceFilter !== 'all') params.file_type = sourceFilter;

        const [docsRes, statsRes] = await Promise.all([
          axios.get(`${API}`, {
            headers: { Authorization: `Bearer ${token}` },
            params
          }),
          axios.get(`${API}/stats`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        setDocuments(docsRes.data.documents || []);
        setCategories(docsRes.data.categories || []);
        setStats(statsRes.data);
      } else {
        // Fetch templates
        const params: any = { include_system: true };
        if (categoryFilter !== 'all') params.category = categoryFilter;

        const res = await axios.get(`${API}/templates`, {
          headers: { Authorization: `Bearer ${token}` },
          params
        });

        setTemplates(res.data.templates || []);
        setCategories(res.data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      const errorMsg = language === 'en' ? 'Could not fetch data' : 'Kunne ikke hente data';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    const confirmMsg = language === 'en' 
      ? 'Are you sure you want to delete this template?' 
      : 'Er du sikker på at du vil slette denne malen?';
    if (!window.confirm(confirmMsg)) return;

    try {
      const token = auth.getToken();
      await axios.delete(`${API}/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const successMsg = language === 'en' ? 'Template deleted' : 'Mal slettet';
      toast.success(successMsg);
      fetchData();
    } catch (error: any) {
      const errorMsg = language === 'en' ? 'Could not delete template' : 'Kunne ikke slette mal';
      toast.error(error.response?.data?.detail || errorMsg);
    }
  };

  const getFileTypeIcon = (fileType?: string) => {
    if (fileType === 'ai_generated') return <Sparkles className="w-4 h-4 text-violet-500" />;
    if (fileType === 'pdf') return <FileCheck className="w-4 h-4 text-red-500" />;
    return <File className="w-4 h-4 text-gray-400" />;
  };

  const getSourceBadge = (source: string) => {
    const badges = {
      case: { 
        label: language === 'en' ? 'Case' : 'Sak', 
        color: 'bg-blue-50 text-blue-700' 
      },
      upload: { 
        label: language === 'en' ? 'Uploaded' : 'Opplastet', 
        color: 'bg-green-50 text-green-700' 
      },
      ai_generated: { 
        label: language === 'en' ? 'AI-generated' : 'AI-generert', 
        color: 'bg-violet-50 text-violet-700' 
      }
    };
    const badge = badges[source] || badges.case;
    return (
      <span className={`px-2 py-0.5 text-[11px] font-medium rounded ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('nb-NO', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white/30" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-8 py-10">
        
        {/* Header */}
        <header className="mb-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
                {t.documents}
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                {activeTab === 'documents' 
                  ? `${stats?.total_documents || 0} ${t.documentsAcrossAllCases}`
                  : `${templates.length} ${t.templatesAvailable}`
                }
              </p>
            </div>
            {activeTab === 'templates' && (
              <button
                className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors"
              >
                <Plus className="w-4 h-4" />
                {t.newTemplate}
              </button>
            )}
          </div>
        </header>

        {/* Stats Row */}
        {activeTab === 'documents' && stats && (
          <div className="flex items-center gap-10 mb-10 pb-8 border-b border-gray-100">
            <div>
              <div className="text-3xl font-semibold text-gray-900">{stats.total_documents}</div>
              <div className="text-sm text-gray-500">{t.total}</div>
            </div>
            <div className="w-px h-10 bg-gray-200" />
            <div>
              <div className="text-3xl font-semibold text-gray-900">{stats.case_files}</div>
              <div className="text-sm text-gray-500">{t.caseDocuments}</div>
            </div>
            <div className="w-px h-10 bg-gray-200" />
            <div>
              <div className="text-3xl font-semibold text-gray-900">{stats.ai_generated}</div>
              <div className="text-sm text-gray-500">{t.aiGenerated}</div>
            </div>
            <div className="w-px h-10 bg-gray-200" />
            <div>
              <div className="text-3xl font-semibold text-gray-900">{stats.uploads}</div>
              <div className="text-sm text-gray-500">{t.uploaded}</div>
            </div>
            <div className="w-px h-10 bg-gray-200" />
            <div>
              <div className="text-3xl font-semibold text-gray-900">{stats.templates.total}</div>
              <div className="text-sm text-gray-500">{t.templates}</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-3 mb-8">
          <button
            onClick={() => setActiveTab('documents')}
            className={`px-4 py-2 text-sm font-medium transition-all border-b-2 ${
              activeTab === 'documents'
                ? 'border-slate-900 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.documents}
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-4 py-2 text-sm font-medium transition-all border-b-2 ${
              activeTab === 'templates'
                ? 'border-slate-900 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.templates}
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex items-center gap-4 mb-8">
          <div className="flex-1 max-w-md relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={activeTab === 'documents' ? t.searchPlaceholder : t.searchTemplatesPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
            />
          </div>

          <div className="flex items-center gap-2">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 bg-gray-50 border-0 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            >
              <option value="all">{t.allCategories}</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            
            {activeTab === 'documents' && (
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="px-3 py-2 bg-gray-50 border-0 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              >
                <option value="all">{t.allSources}</option>
                <option value="case">{t.cases}</option>
                <option value="upload">{t.upload}</option>
                <option value="ai_generated">{t.aiGenerated}</option>
              </select>
            )}

            <div className="flex gap-1 bg-gray-50 p-1 rounded-lg">
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded transition-colors ${
                  viewMode === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded transition-colors ${
                  viewMode === 'grid' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <Grid className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'documents' ? (
          documents.length === 0 ? (
            <div className="text-center py-20">
              <FolderOpen className="w-16 h-16 text-gray-200 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">{t.noDocumentsFound}</h3>
              <p className="text-gray-500 text-sm">
                {searchQuery ? t.tryAnotherSearch : t.documentsWillAppear}
              </p>
            </div>
          ) : viewMode === 'list' ? (
            <div className="space-y-3">
              {documents.map((doc) => (
                <motion.div
                  key={doc.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white border border-gray-100 rounded-lg p-5 hover:border-gray-200 hover:shadow-sm transition-all cursor-pointer group"
                >
                    <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      {getFileTypeIcon(doc.file_type)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                          <h3 className="text-sm font-medium text-gray-900 truncate">
                            {doc.title}
                          </h3>
                          {getSourceBadge(doc.source)}
                        </div>
                        {doc.description && (
                          <p className="text-sm text-gray-500 mb-3 line-clamp-1">{doc.description}</p>
                        )}
                        <div className="flex items-center gap-5 text-xs text-gray-400">
                          {doc.case_title && (
                            <span className="flex items-center gap-1.5">
                              <FileText className="w-3.5 h-3.5" />
                              {doc.case_title}
                            </span>
                          )}
                          {doc.client_name && (
                            <span className="flex items-center gap-1.5">
                              <User className="w-3.5 h-3.5" />
                              {doc.client_name}
                            </span>
                          )}
                          {doc.created_at && (
                            <span className="flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5" />
                              {formatDate(doc.created_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {doc.file_url && (
                      <a
                        href={doc.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-4 p-2 text-gray-400 hover:text-slate-900 hover:bg-gray-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                  </motion.div>
                ))}
              </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documents.map((doc) => (
                <motion.div
                  key={doc.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white border border-gray-100 rounded-lg p-5 hover:border-gray-200 hover:shadow-sm transition-all cursor-pointer group"
                >
                  <div className="flex items-start justify-between mb-4">
                    {getFileTypeIcon(doc.file_type)}
                    {doc.file_url && (
                      <a
                        href={doc.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 text-gray-400 hover:text-slate-900 rounded transition-colors opacity-0 group-hover:opacity-100"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2 truncate">{doc.title}</h3>
                  <p className="text-sm text-gray-500 mb-3 line-clamp-2">{doc.description || t.noDescription}</p>
                  {getSourceBadge(doc.source)}
                  <div className="mt-4 pt-4 border-t border-gray-50 text-xs text-gray-400 space-y-1">
                    {doc.case_title && <div className="truncate">{doc.case_title}</div>}
                    {doc.created_at && <div>{formatDate(doc.created_at)}</div>}
                  </div>
                </motion.div>
              ))}
            </div>
          )
        ) : (
          templates.length === 0 ? (
            <div className="text-center py-20">
              <BookTemplate className="w-16 h-16 text-gray-200 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">{t.noTemplatesFound}</h3>
              <p className="text-gray-500 text-sm mb-6">{t.createFirstTemplate}</p>
              <button className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors mx-auto">
                <Plus className="w-4 h-4" />
                {t.createTemplate}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {templates.map((template) => (
                <motion.div
                  key={template.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white border border-gray-100 rounded-lg p-5 hover:border-gray-200 hover:shadow-sm transition-all group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <BookTemplate className="w-5 h-5 text-orange-500 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1.5">
                          <h3 className="text-sm font-medium text-gray-900">{template.title}</h3>
                          {template.is_system && (
                            <span className="px-2 py-0.5 text-[10px] font-medium rounded bg-blue-50 text-blue-700">
                              {t.system}
                            </span>
                          )}
                        </div>
                        {template.description && (
                          <p className="text-sm text-gray-500 mb-3">{template.description}</p>
                        )}
                        <div className="flex items-center gap-5 text-xs text-gray-400">
                          <span className="flex items-center gap-1.5">
                            <Tag className="w-3.5 h-3.5" />
                            {template.category}
                          </span>
                          {template.variables.length > 0 && (
                            <span>{template.variables.length} {t.variables}</span>
                          )}
                          <span>{template.usage_count} {t.timesUsed}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button className="px-4 py-2 text-sm font-medium text-slate-900 hover:bg-gray-50 rounded-lg transition-colors">
                        {t.useTemplate}
                      </button>
                      {!template.is_system && (
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default DocumentsViewMinimal;
